import { create } from 'zustand'
import type { Site, Floor, Location, Wall, IFCWall, PathSegment, Badge } from '../types'
import { siteApi } from '../services/siteApi'
import { zoneApi } from '../services/zoneApi'
import { wallApi } from '../services/wallApi'
import { pathApi } from '../services/pathApi'
import { badgeApi } from '../services/badgeApi'
import { usePermissionStore } from './permissionStore'

interface BuildingState {
  sites: Site[]
  currentSiteId: string | null
  floors: Floor[]
  locations: Location[]
  walls: Wall[]
  ifcWalls: IFCWall[]
  pathSegments: PathSegment[]
  badges: Badge[]
  loading: boolean
  error: string | null
  fetchSites: (clientId?: string) => Promise<void>
  loadSite: (siteId: string) => Promise<void>
  resetSite: () => void
  resetAll: () => void
}

export const useBuildingStore = create<BuildingState>((set) => ({
  sites: [],
  currentSiteId: null,
  floors: [],
  locations: [],
  walls: [],
  ifcWalls: [],
  pathSegments: [],
  badges: [],
  loading: false,
  error: null,

  fetchSites: async (clientId?: string) => {
    console.log('[buildingStore] fetchSites...', clientId ? `client=${clientId}` : 'all')
    try {
      const sites = await siteApi.getSites(clientId)
      console.log('[buildingStore] sites loaded:', sites.length, sites.map((s) => s.name))
      set({ sites })
    } catch (e) {
      console.error('[buildingStore] fetchSites error:', e)
      set({ error: e instanceof Error ? e.message : 'Erreur chargement sites' })
    }
  },

  resetSite: () => {
    set({
      currentSiteId: null,
      floors: [],
      locations: [],
      walls: [],
      ifcWalls: [],
      pathSegments: [],
      badges: [],
    })
  },

  resetAll: () => {
    set({
      sites: [],
      currentSiteId: null,
      floors: [],
      locations: [],
      walls: [],
      ifcWalls: [],
      pathSegments: [],
      badges: [],
      loading: false,
      error: null,
    })
  },

  loadSite: async (siteId: string) => {
    console.log('[buildingStore] loadSite:', siteId)
    set({ loading: true, error: null, currentSiteId: siteId })
    const can = usePermissionStore.getState().hasPermission
    try {
      const [floors, locations, walls, ifcWalls, pathSegments, badges] = await Promise.all([
        siteApi.getFloors(siteId),
        can('locations', 'read') ? zoneApi.getLocations(siteId) : ([] as Location[]),
        can('walls', 'read') ? wallApi.getWalls(siteId) : ([] as Wall[]),
        can('walls', 'read') ? wallApi.getIFCWalls(siteId).catch(() => [] as IFCWall[]) : ([] as IFCWall[]),
        can('paths', 'read') ? pathApi.getSegments(siteId).catch(() => [] as PathSegment[]) : ([] as PathSegment[]),
        can('badges', 'read') ? badgeApi.getBadges(siteId).catch(() => [] as Badge[]) : ([] as Badge[]),
      ])
      console.log('[buildingStore] site loaded:', {
        floors: floors.length,
        locations: locations.length,
        walls: walls.length,
        ifcWalls: ifcWalls.length,
        pathSegments: pathSegments.length,
        badges: badges.length,
      })
      set({ floors, locations, walls, ifcWalls, pathSegments, badges, loading: false })
    } catch (e) {
      console.error('[buildingStore] loadSite error:', e)
      set({ error: e instanceof Error ? e.message : 'Erreur chargement site', loading: false })
    }
  },
}))
